import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmtF = (f) => (f ? f.slice(0, 10).split("-").reverse().join("/") : "s/f");
const OK_EXT = /\.(docx|pdf|txt)$/i;

async function archivosDeDrop(dt) {
  const out = [];
  const leer = (entry, ruta) => new Promise((res) => {
    if (entry.isFile) entry.file((f) => { out.push(Object.assign(f, { ruta: ruta + f.name })); res(); }, res);
    else if (entry.isDirectory) {
      const reader = entry.createReader();
      const lote = () => reader.readEntries(async (es) => { if (!es.length) return res(); for (const e of es) await leer(e, ruta + entry.name + "/"); lote(); }, res);
      lote();
    } else res();
  });
  const entries = [...dt.items].map((i) => i.webkitGetAsEntry?.()).filter(Boolean);
  if (entries.length) await Promise.all(entries.map((e) => leer(e, "")));
  else out.push(...dt.files);
  return out.filter((f) => OK_EXT.test(f.name));
}

export default function CargaMasiva({ onDone }) {
  const [filas, setFilas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const dirRef = useRef();

  const enviar = async (f, codigo) => {
    const fd = new FormData();
    fd.append("file", f, f.name);
    if (codigo) fd.append("codigo", codigo);
    const r = await api.post("/inmuebles/informe", fd);
    return r.data;
  };

  const procesar = async (files) => {
    if (!files.length) return toast.error("No hay archivos .docx/.pdf/.txt");
    setBusy(true);
    const iniciales = files.map((f) => ({ archivo: f.ruta || f.name, f, estado: "pendiente" }));
    setFilas(iniciales);
    for (let i = 0; i < iniciales.length; i++) {
      let fila;
      try {
        const r = await enviar(files[i]);
        fila = r.confirmar ? { ...iniciales[i], estado: "confirmar", ...r } : { ...iniciales[i], ...r, estado: r.estado === "pendiente" ? "pendiente_vincular" : "ok" };
      } catch (e) { fila = { ...iniciales[i], estado: "error", detalle: e?.response?.data?.detail || "Error" }; }
      setFilas((prev) => prev.map((x, j) => (j === i ? fila : x)));
    }
    setBusy(false); onDone?.();
  };

  const confirmar = async (idx, codigo) => {
    if (!codigo) return;
    try {
      const r = await enviar(filas[idx].f, codigo);
      setFilas((prev) => prev.map((x, j) => (j === idx ? { ...x, ...r, estado: r.estado === "pendiente" ? "pendiente_vincular" : "ok" } : x)));
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo vincular"); }
  };

  const res = { ok: filas.filter((x) => x.estado === "ok").length, pend: filas.filter((x) => x.estado === "pendiente_vincular").length, confirmar: filas.filter((x) => x.estado === "confirmar").length, error: filas.filter((x) => x.estado === "error").length };
  return (
    <div className="space-y-2" data-testid="inm-carga-masiva">
      <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={async (e) => { e.preventDefault(); setOver(false); procesar(await archivosDeDrop(e.dataTransfer)); }}
        className={`border-2 border-dashed rounded-lg p-4 text-center ${over ? "border-emerald-600 bg-emerald-50" : "border-slate-300"}`} data-testid="inm-dropzone">
        <b>Carga masiva:</b> arrastrá acá una carpeta o varios informes (.docx, .pdf, .txt)
        <div className="mt-2 flex justify-center gap-2 flex-wrap">
          <button type="button" onClick={() => dirRef.current?.click()} className="rounded px-3 py-1 bg-slate-700 text-white" disabled={busy} data-testid="inm-elegir-carpeta">Elegir carpeta</button>
          <input ref={dirRef} type="file" multiple webkitdirectory="" directory="" className="hidden" onChange={(e) => procesar([...e.target.files].filter((f) => OK_EXT.test(f.name)))} data-testid="inm-carpeta-input" />
          <label className="rounded px-3 py-1 bg-slate-200 cursor-pointer">Elegir archivos<input type="file" multiple accept=".docx,.pdf,.txt" className="hidden" onChange={(e) => procesar([...e.target.files])} data-testid="inm-archivos-input" /></label>
        </div>
      </div>
      {filas.length > 0 && <div className="overflow-x-auto" data-testid="inm-masiva-resultados">
        <div className="text-slate-600 mb-1" data-testid="inm-masiva-resumen">{busy ? "Procesando… " : ""}{res.ok} vinculados · {res.pend} pendientes de vincular · {res.confirmar} a confirmar · {res.error} con error · {filas.length} archivos</div>
        <table className="w-full text-xs"><thead><tr className="text-left text-slate-500"><th className="py-1">Archivo</th><th>Estado</th><th>Cliente vinculado</th><th>Inmuebles</th><th>Informe</th></tr></thead>
          <tbody>{filas.map((x, i) => <tr key={i} className="border-t border-slate-100" data-testid={`inm-masiva-fila-${i}`}>
            <td className="py-1 font-mono">{x.archivo}</td>
            <td>{x.estado === "pendiente" && <span className="text-slate-400">pendiente</span>}{x.estado === "ok" && <span className="text-emerald-700 font-semibold">vinculado</span>}{x.estado === "pendiente_vincular" && <span className="text-amber-700 font-semibold">pendiente de vincular (código {x.codigo})</span>}{x.estado === "confirmar" && <span className="text-amber-700 font-semibold">a confirmar</span>}{x.estado === "error" && <span className="text-red-700 font-semibold">error</span>}</td>
            <td>{x.estado === "ok" && `${x.cliente} (${x.codigo})`}{x.estado === "pendiente_vincular" && <span className="text-amber-800">{x.cliente} · {x.aviso}</span>}
              {x.estado === "confirmar" && <span>Titular "{(x.titulares || []).join(", ")}": {(x.candidatos || []).map((c) => <button key={c.codigo} onClick={() => confirmar(i, c.codigo)} className="underline mr-2" data-testid={`inm-masiva-cand-${i}-${c.codigo}`}>{c.cliente}</button>)}
                <input placeholder="código" className="border rounded px-1 w-24" onKeyDown={(e) => e.key === "Enter" && confirmar(i, e.target.value)} data-testid={`inm-masiva-codigo-${i}`} /></span>}
              {x.estado === "error" && <span className="text-red-700">{x.detalle}</span>}</td>
            <td>{x.inmuebles ?? ""}</td><td>{x.fecha ? fmtF(x.fecha) : ""}</td>
          </tr>)}</tbody></table>
      </div>}
    </div>
  );
}
